// @ts-nocheck

import { theme, AlertModal } from 'applaus-ui-kit'
import { useEffect, useRef } from 'react'
import GlobalStyle from './styles/global'
import { Router } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import Routes from './routes/routes'
import history from './routes/services/history'
import { useCookies } from 'react-cookie'
import { decode } from 'jsonwebtoken'
import { useDispatch, useSelector } from 'react-redux'
import {
  authenticationFailed,
  authenticationRequest,
  setUserAction
} from './redux/User/actions'
import { getCustomerDataService } from './api/services/profile/getCustomerDataService'
import { ReduxState } from './redux/rootReducer'
import { getCustomerInfo } from './api/services/profile/getCustomerInfo'
import { clearModalState } from './redux/Modal/Actions/actionsModal'
import { Loading } from './components/loading/Loading'
import { listCoursesRequestAction } from './redux/Content/actions'
import { purchasesOrderCustomerRequest } from './redux/Purchases/actions'
import { clearLoading, setLoading } from './redux/Loading/actions'
import { initMetaPixel, trackMetaPageView } from './utils/metaPixel'

function App() {
  const [getCookie] = useCookies(['applaus-auth-customer'])
  const dispatch = useDispatch()
  const hydrateInFlight = useRef(false)

  const { UserReducer, ModalReducer, LoadingReducer } = useSelector(
    (state: ReduxState) => state
  )

  const hydrateFromCookie = async (authCookie) => {
    if (hydrateInFlight.current) return
    hydrateInFlight.current = true

    try {
      const decoded = decode(authCookie.accessToken)
      if (!decoded || typeof decoded === 'string') {
        dispatch(authenticationFailed())
        dispatch(clearLoading())
        return
      }

      const data = await getCustomerDataService(authCookie.accessToken)

      if (!data) {
        dispatch(authenticationFailed())
        dispatch(clearLoading())
        return
      }

      dispatch(
        setUserAction({
          ...data,
          accessToken: authCookie.accessToken,
          refreshToken: authCookie.refreshToken,
          isAuthenticated: true,
          loading: false
        })
      )

      if (history.location.pathname.includes('/purchases')) {
        dispatch(purchasesOrderCustomerRequest())
      }
      dispatch(clearLoading())
    } catch (e) {
      dispatch(authenticationFailed())
      dispatch(clearLoading())
    } finally {
      hydrateInFlight.current = false
    }
  }

  const initialRequest = async () => {
    const authCookie = getCookie['applaus-auth-customer']

    if (authCookie) {
      await hydrateFromCookie(authCookie)
    } else if (UserReducer.accessToken) {
      try {
        const data = await getCustomerInfo()
        dispatch(
          setUserAction({ ...data, loading: false, isAuthenticated: true })
        )
        dispatch(clearLoading())
      } catch (e) {
        dispatch(authenticationFailed())
        dispatch(clearLoading())
      }
    } else {
      dispatch(authenticationFailed())
      dispatch(clearLoading())
    }
  }

  useEffect(() => {
    if (!history.location.pathname.includes('/mobileCheckout')) {
      if (UserReducer.accessToken && UserReducer.isAuthenticated) {
        dispatch(listCoursesRequestAction())
        initialRequest()
      } else {
        dispatch(authenticationRequest())
        dispatch(setLoading())
        dispatch(listCoursesRequestAction())
        initialRequest()
      }
    }
  }, [])

  // Cookie can be unavailable on first paint; hydrate when it appears.
  useEffect(() => {
    const authCookie = getCookie['applaus-auth-customer']
    if (
      authCookie &&
      !UserReducer.accessToken &&
      !UserReducer.isAuthenticated
    ) {
      dispatch(authenticationRequest())
      dispatch(setLoading())
      hydrateFromCookie(authCookie)
    } else if (!authCookie && UserReducer.loading && !UserReducer.accessToken) {
      const timer = setTimeout(() => {
        if (!getCookie['applaus-auth-customer'] && !UserReducer.accessToken) {
          dispatch(authenticationFailed())
          dispatch(clearLoading())
        }
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [getCookie['applaus-auth-customer']])

  useEffect(() => {
    initMetaPixel()
    trackMetaPageView()

    const unlisten = history.listen(() => {
      trackMetaPageView()
    })

    return () => unlisten()
  }, [])

  return (
    <Router history={history}>
      <ThemeProvider theme={theme}>
        <Routes />

        <GlobalStyle />
        <AlertModal
          open={ModalReducer.open}
          variant={ModalReducer.variant}
          message={ModalReducer.message}
          description={ModalReducer.description}
          buttonText={ModalReducer.buttonText ? ModalReducer.buttonText : 'Ok'}
          click={
            ModalReducer.click
              ? ModalReducer.click
              : () => dispatch(clearModalState())
          }
        />

        <Loading
          isLoading={LoadingReducer.isLoading || UserReducer.loading}
          blur={LoadingReducer.blur}
        />
      </ThemeProvider>
    </Router>
  )
}

export default App
